import { Store } from '@ngrx/store';
import { AuthActions } from '../store/auth/auth.actions';
import { inject } from '@angular/core';
import { MAINTENANCE_PERIOD } from '../constants/constants';
import { TokenService } from './token.service';
import { DevToolsDetectorService } from './dev-tools-detector.service';
import { ServiceWorkerUpdateService } from './service-worker-update.service';
import { DateUtils } from '../../shared/utils/date.utils';
import { TraceService } from '@sentry/angular';
import { DbService } from '../db/db.service';
import { NetworkDiagnosticsService } from './network-diagnostics.service';
import { AssetsPerformanceMonitoringService } from './assets-performance-monitoring.service';
import { LoginAsEmployeePayload } from '../models/login-as-employee-payload';

export const initializeApp = (isReportBuilderInstance: boolean) => {
  const store = inject(Store);
  const tokenService = inject(TokenService);

  if (!isReportBuilderInstance) {
    // Sentry
    inject(TraceService);

    // Service worker update service
    inject(ServiceWorkerUpdateService).init();

    // Network Diagnostic Service
    inject(NetworkDiagnosticsService).init();

    // Monitor slow loading assets and report to sentry
    inject(AssetsPerformanceMonitoringService).init();
  }
  // Dev tools detector
  inject(DevToolsDetectorService).setupDevToolsDetector();

  // Streaming setup init
  // inject(WebClientService).init();

  // DB setup
  inject(DbService).init();

  // Maintenance Mode Logic
  if (
    DateUtils.new().isAfter(DateUtils.new(MAINTENANCE_PERIOD.start)) &&
    DateUtils.new().isBefore(DateUtils.new(MAINTENANCE_PERIOD.end))
  ) {
    store.dispatch(AuthActions.setMaintenanceMode());
    return;
  }

  if (isReportBuilderInstance) {
    store.dispatch(AuthActions.setAsReportBuilderInstance());
  }

  const searchParams = new URL(window.location.href).searchParams;
  const loginAsToken = searchParams.get('login-as');
  const employeeLoginAs = searchParams.get('employee-login-as');
  if (loginAsToken) {
    const [customerId, username, password] = window.atob(loginAsToken).split('|');
    if (customerId && username && password) {
      store.dispatch(
        AuthActions.authenticate({
          customerId: +customerId,
          username,
          password,
        })
      );
      store.dispatch(AuthActions.setLoggedInAs());
    }
  } else if (employeeLoginAs) {
    const {
      idt,
      mpt,
      lst,
      cid,
      did,
      dn,
      sid,
      sn,
      at,
      sIds,
      isb,
      sr,
      cs,
      rdTo,
    } = JSON.parse(window.atob(employeeLoginAs)) as LoginAsEmployeePayload;
    store.dispatch(
      AuthActions.setEmployeeLoggedInAs({
        data: {
          identityToken: idt,
          mpToken: mpt,
          lsToken: lst,
          customerId: cid,
          districtId: did,
          districtName: dn,
          schoolId: sid,
          schoolName: sn,
          adminType: at,
          schoolIds: sIds,
          isSandbox: isb,
          salesRep: sr,
          customerSuccess: cs,
          redirectTo: rdTo,
        },
      })
    );

    // Check if payload have LS and MP tokens, if yes then reuse those tokens
    if (mpt && lst) {
      store.dispatch(AuthActions.authenticateLSMP({ token: idt, lsToken: lst, mpToken: mpt }));
    } else {
      store.dispatch(AuthActions.authenticateLSMP({ token: idt }));
    }
  } else if (tokenService.getAuthTokenByKey('idToken')) {
    const token = tokenService.getAuthToken();
    // Check if payload have LS and MP tokens, if yes then reuse those tokens
    if (token.idToken && token.mpToken && token.lsToken) {
      store.dispatch(AuthActions.authenticateLSMP({ token: token.idToken, lsToken: token.lsToken, mpToken: token.mpToken }));
    } else {
      store.dispatch(AuthActions.authenticateLSMP({ token: token.idToken }));
    }
  } else {
    const exchangeId = searchParams.get('exchangeId');
    const exchangeProvider = searchParams.get('exchangeProvider');
    const identityServerToken = searchParams.get('it');
    if (exchangeId && exchangeProvider) {
      store.dispatch(AuthActions.authenticateViaTokenExchange({exchangeId, exchangeProvider}));
    } else if (identityServerToken) {
      store.dispatch(AuthActions.authenticateViaAccessToken({token: identityServerToken}));
    }
  }
};
